import {
  Attachments,
  Bubble,
  Conversations,
  Prompts,
  Sender,
  Welcome,
  useXAgent,
  useXChat,
} from "@ant-design/x";
import { createStyles } from "antd-style";
import React, { useEffect, useState, useRef } from "react";
import OpenAI from "openai";
import { XStream } from "@ant-design/x";
import markdownit from "markdown-it";
import { Typography } from "antd";
import { BrowserRouter as Router, Route, Routes, useNavigate } from "react-router-dom";
import Login from "./Login";
import Dashboard from "./pages/Dashboard";

import {
  CloudUploadOutlined,
  CommentOutlined,
  SettingOutlined,
  FireOutlined,
  HeartOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReadOutlined,
  LoginOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import { Badge, Button, type GetProp, Space, Drawer, message, Flex } from "antd";

// 初始化markdown解析器
const md = markdownit({ html: true, breaks: true });

// markdown渲染函数
const renderMarkdown = (content: string) => (
  <Typography>
    <div dangerouslySetInnerHTML={{ __html: md.render(content) }} />
  </Typography>
);

const renderTitle = (icon: React.ReactElement, title: string) => (
  <Space align="start">
    {icon}
    <span>{title}</span>
  </Space>
);

// 修改对话项结构，增加conversationId字段
interface ConversationItem {
  key: string;
  label: string;
  conversationId?: string;
}

const defaultConversationsItems: ConversationItem[] = [
  {
    key: "0",
    label: "什么是炣森科技HolyAI？",
  },
];

const useStyle = createStyles(({ token, css }) => {
  return {
    layout: css`
      width: 100%;
      height: 100vh;
      border-radius: ${token.borderRadius}px;
      display: flex;
      background: ${token.colorBgContainer};
      font-family: AlibabaPuHuiTi, ${token.fontFamily}, sans-serif;
      overflow: hidden;
      
      @media (max-width: 768px) {
        flex-direction: column;
      }

      .ant-prompts {
        color: ${token.colorText};
      }
    `,
    menu: css`
      background: ${token.colorBgLayout}80;
      width: 280px;
      height: 100%;
      display: flex;
      flex-direction: column;
      
      @media (max-width: 768px) {
        display: none;
      }
    `,
    mobileHeader: css`
      display: none;
      padding: 12px;
      background: ${token.colorBgContainer};
      border-bottom: 1px solid ${token.colorBorderSecondary};
      
      @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    `,
    drawerMenu: css`
      .ant-drawer-body {
        padding: 0;
      }
    `,
    conversations: css`
      padding: 0 12px;
      flex: 1;
      overflow-y: auto;
    `,
    beianInfo: css`
      padding: 12px;
      text-align: center;
      font-size: 12px;
      color: ${token.colorTextSecondary};
      margin-top: auto;
      
      a {
        color: ${token.colorTextSecondary};
        &:hover {
          color: ${token.colorPrimary};
        }
      }
    `,
    chat: css`
      height: 100%;
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      padding: ${token.paddingLG}px;
      gap: 16px;
      
      @media (max-width: 768px) {
        padding: ${token.paddingSM}px;
        height: calc(100% - 56px);
      }
      
      @media (min-width: 769px) {
        max-width: 700px;
      }
    `,
    messages: css`
      flex: 1;
      overflow-y: auto;
    `,
    placeholder: css`
      padding-top: 32px;
      
      @media (max-width: 768px) {
        padding-top: 16px;
      }
    `,
    sender: css`
      box-shadow: ${token.boxShadow};
    `,
    logo: css`
      display: flex;
      height: 72px;
      align-items: center;
      justify-content: start;
      padding: 0 24px;
      box-sizing: border-box;

      img {
        width: 24px;
        height: 24px;
        display: inline-block;
      }

      span {
        display: inline-block;
        margin: 0 8px;
        font-weight: bold;
        color: ${token.colorText};
        font-size: 16px;
      }
    `,
    mobileLogo: css`
      display: flex;
      align-items: center;

      img {
        width: 24px;
        height: 24px;
        display: inline-block;
      }

      span {
        display: inline-block;
        margin: 0 8px;
        font-weight: bold;
        color: ${token.colorText};
        font-size: 16px;
      }
    `,
    addBtn: css`
      background: #1677ff0f;
      border: 1px solid #1677ff34;
      width: calc(100% - 24px);
      margin: 0 12px 24px 12px;
    `,
    chatPrompt: css`
      .ant-prompts-label {
        color: #000000e0 !important;
      }
      .ant-prompts-desc {
        color: #000000a6 !important;
        width: 100%;
      }
      .ant-prompts-icon {
        color: #000000a6 !important;
      }
    `,
  };
});

const placeholderPromptsItems: GetProp<typeof Prompts, "items"> = [
  {
    key: "1",
    label: renderTitle(
      <FireOutlined style={{ color: "#FF4D4F" }} />,
      "您可以这么问"
    ),
    description: "关于HolyAI",
    children: [
      {
        key: "1-1",
        description: `你是谁`,
      },
      {
        key: "1-2",
        description: `你有哪些能力`,
      },
      {
        key: "1-3",
        description: `你来自哪里`,
      },
    ],
  },
  {
    key: "2",
    label: renderTitle(
      <ReadOutlined style={{ color: "#1890FF" }} />,
      "关于炣森科技"
    ),
    description: "公司介绍",
    children: [
      {
        key: "2-1",
        description: `解决的痛点`,
      },
      {
        key: "2-2",
        description: `公司的业务`,
      },
      {
        key: "2-3",
        description: `创始人`,
      },
    ],
  },
];

// 将常量改为useState初始值
const senderPromptsItems: GetProp<typeof Prompts, "items"> = [
  // {
  //   key: "1",
  //   description: "退役安置办法",
  // },
  // {
  //   key: "2",
  //   description: "退役保障政策",
  // },
];

// 修改roles定义，将needtyping的使用移动到AppContent组件内
const roles = (needtyping: boolean): GetProp<typeof Bubble.List, "roles"> => ({
  ai: {
    placement: "start",
    typing: needtyping ? { step: 5, interval: 20 } : false,
    styles: {
      content: {
        backgroundColor: "rgb(221, 243, 253)",
        borderRadius: 16,
      },
    },
    avatar: <img src="https://kersen.tech/holyai.png" style={{ width: 32, height: 32 }} />,
    messageRender: renderMarkdown,
  },
  local: {
    placement: "end",
    variant: "shadow",
  },
});

// const client = new OpenAI({
//   baseURL: "https://dashscope.aliyuncs.com/compatible-mode/v1",
//   apiKey: "sk-aede76512bea4698ba2cce39ebbee566", // 接入模型API
//   dangerouslyAllowBrowser: true,
// });

// 独立组件，需要包装在Router中使用
const AppContent = () => {
  // ==================== Style ====================
  const { styles } = useStyle();
  const navigate = useNavigate();

  // ==================== State ====================
  const [headerOpen, setHeaderOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [content, setContent] = useState("");
  const [conversationsItems, setConversationsItems] = useState<ConversationItem[]>(
    defaultConversationsItems
  );
  const [activeKey, setActiveKey] = useState(
    defaultConversationsItems[0].key
  );
  const [attachedFiles, setAttachedFiles] = useState<
    GetProp<typeof Attachments, "items">
  >([]);
  // 使用useState和useRef共同管理conversationId
  const [conversationId, setConversationId] = useState<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  // 添加一个状态来追踪是否收到了第一个数据块
  const [receivedFirstChunk, setReceivedFirstChunk] = useState(false);
  // 添加AbortController引用
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // 将needtyping的状态定义移到组件内部
  const [needtyping, setNeedtyping] = useState(true);

  // 添加滚动到底部的函数，参考example_scroll.tsx
  const scrollToBottom = () => {
    // 首先尝试找到Bubble.List内部的滚动容器
    const bubbleList = document.querySelector('.ant-bubble-list');
    const messageContainer = bubbleList || document.querySelector(`.${styles.messages}`);
    
    if (!messageContainer) {
      return;
    }

    const supportsSmooth = 'scrollBehavior' in document.documentElement.style;

    messageContainer.scrollTo({
      top: messageContainer.scrollHeight, 
      behavior: supportsSmooth ? 'smooth' : 'auto',
    });
  };

  // 保持ref与state同步
  useEffect(() => {
    conversationIdRef.current = conversationId;
    console.log("conversationId状态已更新:", conversationId);
    
    // 如果conversationId存在且activeKey有效，更新conversationsItems中对应项的conversationId
    if (conversationId && activeKey) {
      setConversationsItems(prev => 
        prev.map(item => 
          item.key === activeKey 
            ? { ...item, conversationId } 
            : item
        )
      );
      console.log(`已将conversationId ${conversationId} 绑定到对话 ${activeKey}`);
    }
  }, [conversationId]);

  // ==================== Runtime ====================
  const [agent] = useXAgent({
    request: async (info, callbacks) => {
      const { messages, message } = info;
      const { onSuccess, onUpdate, onError } = callbacks;

      // current message
      console.log("message", message);

      // history messages
      console.log("messages", messages);

      let fullContent = "";
      try {
        // 在开始请求时重置接收状态
        setReceivedFirstChunk(false);
        
        // 创建新的AbortController并保存到ref
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;
        
        // 使用ref获取最新的conversationId值
        const currentConversationId = conversationIdRef.current;
        console.log("当前conversation_id (从ref获取):", currentConversationId);
        
        // 构建URL，如果有conversationId则添加为查询参数
        const url = currentConversationId 
          ? `https://api.coze.cn/v3/chat?conversation_id=${currentConversationId}`
          : "https://api.coze.cn/v3/chat";
        
        console.log("请求URL:", url);
        
        // 准备请求体
        const requestBody: any = {
          bot_id: "7500858261399552040",
          user_id: "7486123734983229450",
          stream: true,
          auto_save_history: true,
          additional_messages: [{ role: "user", content: message}],
        };
        
        console.log("完整请求体:", JSON.stringify(requestBody));
        
        const res = await fetch(url, {
          method: "POST",
          headers: {
            Authorization:
              "Bearer sat_D9qpYdh04WcvRlXtEXQgEpVgTqX9G39u0RccaeDmOV0kL8TmHM13Z0Ofj0d1S5Bx",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal, // 添加signal用于中断请求
        });

        if (!res.ok) {
          throw new Error(`API返回错误：${res.status} ${res.statusText}`);
        }


        for await (const chunk of XStream({ readableStream: res.body! })) {
          // 检查是否已被中断
          if (signal.aborted) {
            console.log("请求已被中断");
            break;
          }
          
          if (chunk.event === "conversation.chat.created") {
            console.log("收到chat创建事件:", chunk.data);
            try {
              const chatData = JSON.parse(chunk.data);
              if (chatData && chatData.conversation_id) {
                console.log("更新conversation_id:", chatData.conversation_id);
                // 更新ref和state
                conversationIdRef.current = chatData.conversation_id;
                setConversationId(chatData.conversation_id);
              }
            } catch (e) {
              console.error("解析创建事件数据失败:", e);
            }
          }
          
          // 检查是否有数据字段
          if (chunk.data) {
            // 检查不同的可能格式
            if (chunk.event === "conversation.message.delta") {
              try {
                const deltaData = JSON.parse(chunk.data);
                deltaData.content = deltaData.content.replace("\\n\\n", "\n\n  ");
                fullContent += deltaData.content;
                
                // 标记已收到第一个数据块
                if (!receivedFirstChunk) {
                  setReceivedFirstChunk(true);
                }
                
                onUpdate(fullContent);
              } catch (e) {
                console.error("解析delta数据失败:", e);
              }
            }
          }
        }

        if (!fullContent) {
          console.warn("未收到任何回复内容，返回默认回复");
          fullContent = "抱歉，我暂时无法回答您的问题。请稍后再试。";
        }

        //console.log("完整回复内容:", fullContent);
        console.log("请求完成后的conversation_id:", conversationIdRef.current);
        
        // 如果请求被中断，只更新到目前为止的内容，但不标记为成功完成
        onSuccess(fullContent);

      } catch (error) {
        // 如果是AbortError，不将其视为错误
        if ((error as any).name === 'AbortError') {
          console.log("请求被用户中断");
          onSuccess(fullContent);
          onError(error as Error);
        } else {
          // 处理其他错误
          console.error("API请求失败:", error);
          onError(error as Error);
        }
      } finally {
        // 清理AbortController
        abortControllerRef.current = null;
      }
    },
  });

  const { onRequest, messages, setMessages } = useXChat({
    agent,
  });

  useEffect(() => {
    if (activeKey !== undefined) {
      // 查找对应的会话项
      const activeConversation = conversationsItems.find(item => item.key === activeKey);
      
      // 如果找到了会话项且它有conversationId
      if (activeConversation && activeConversation.conversationId) {
        console.log(`切换到现有对话 ${activeKey}，关联的conversationId是 ${activeConversation.conversationId}`);
        // 更新当前conversationId
        setConversationId(activeConversation.conversationId);
        conversationIdRef.current = activeConversation.conversationId;
        
        // 获取历史消息
        fetchHistoryMessages(activeConversation.conversationId);
      } else {
        // 如果是新对话或没有conversationId的对话
        setMessages([]);
        setNeedtyping(true);
        // 重置conversationId的state和ref
        setConversationId(null);
        conversationIdRef.current = null;
        console.log("新建对话或切换到无历史的对话，重置conversation_id为null");
      }
    }
  }, [activeKey]);

  // 获取历史消息的函数
  const fetchHistoryMessages = async (conversationId: string) => {
    try {
      console.log(`正在获取对话 ${conversationId} 的历史消息...`);
      
      const response = await fetch(`https://api.coze.cn/v1/conversation/message/list?conversation_id=${conversationId}`, {
        method: 'POST',
        headers: {
          Authorization:
            "Bearer sat_D9qpYdh04WcvRlXtEXQgEpVgTqX9G39u0RccaeDmOV0kL8TmHM13Z0Ofj0d1S5Bx",
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`获取历史消息失败：${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("历史消息API返回数据:", data);
      
      // 定义消息类型
      interface HistoryMessageItem {
        id: string;
        content: string;
        role: string;
        created_at: number;
        [key: string]: any;
      }
      
      // 确保返回了有效数据
      if (data && data.data && Array.isArray(data.data) && data.data.length > 0) {
        // 根据API返回的数据结构处理历史消息
        // 注意：API返回的消息顺序可能是倒序的，需要进行排序
        const historyMessages = data.data
          .sort((a: HistoryMessageItem, b: HistoryMessageItem) => a.created_at - b.created_at) // 按时间顺序排序
          .map((msg: HistoryMessageItem) => ({
            id: msg.id,
            message: msg.content,
            status: msg.role === 'user' ? 'local' : 'done',
          }));
        
        console.log("处理后的历史消息:", historyMessages);
        
        // 更新消息列表
        setNeedtyping(false);
        setMessages(historyMessages);
        message.success("已加载历史消息");
      } else {
        console.log("没有找到历史消息或数据格式不正确");
        message.info("没有找到历史消息");
      }
    } catch (error) {
      console.error("获取历史消息失败:", error);
      message.error("获取历史消息失败");
    }
  };

  useEffect(() => {
    // 初始化时重置conversationId的state和ref
    setConversationId(null);
    conversationIdRef.current = null;
    console.log("组件初始化，重置conversation_id为null");
  }, []);

  // ==================== Event ====================
  const onSubmit = (nextContent: string) => {
    if (!nextContent) return;
    
    // 检查当前会话是否有消息，如果没有则更新会话标题为消息前10个字
    if (messages.length === 0 && activeKey !== undefined) {
      // 截取消息的前10个字作为标题，如果不足10个字则全部使用
      const newLabel = nextContent.length > 10 ? nextContent.substring(0, 10) + "..." : nextContent;
      
      // 更新conversationsItems中当前会话的label
      setConversationsItems(prev => 
        prev.map(item => 
          item.key === activeKey 
            ? { ...item, label: newLabel } 
            : item
        )
      );
    }
    
    onRequest(nextContent);
    setContent("");
    // 在移动端提交后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
    // 提交后稍等一下再滚动，确保消息已添加
    setTimeout(scrollToBottom, 50);
  };

  const onPromptsItemClick: GetProp<typeof Prompts, "onItemClick"> = (info) => {
    // 检查当前会话是否有消息，如果没有则更新会话标题为提示词内容的前10个字
    if (messages.length === 0 && activeKey !== undefined) {
      const description = info.data.description as string;
      // 截取提示词内容的前10个字作为标题
      const newLabel = description.length > 10 ? description.substring(0, 10) + "..." : description;
      
      // 更新conversationsItems中当前会话的label
      setConversationsItems(prev => 
        prev.map(item => 
          item.key === activeKey 
            ? { ...item, label: newLabel } 
            : item
        )
      );
    }

    onRequest(info.data.description as string);
    // 在移动端点击提示词后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
    // 点击提示词后稍等一下再滚动，确保消息已添加
    setTimeout(scrollToBottom, 50);
  };



  const onAddConversation = () => {
    handleCancelRequest();
    setConversationId(null);
    setConversationsItems([
      ...conversationsItems,
      {
        key: `${conversationsItems.length}`,
        label: `新咨询对话 ${conversationsItems.length}`,
      },
    ]);
    setActiveKey(`${conversationsItems.length}`);
    // 在移动端添加会话后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const onConversationClick: GetProp<typeof Conversations, "onActiveChange"> = (
    key
  ) => {
    handleCancelRequest();
    setActiveKey(key);
    // 在移动端选择会话后关闭抽屉
    if (drawerVisible) {
      setDrawerVisible(false);
    }
  };

  const handleFileChange: GetProp<typeof Attachments, "onChange"> = (info) =>
    setAttachedFiles(info.fileList);
    
  // 处理登录按钮点击
  const handleLoginClick = () => {
    navigate('/login');
  };

  // 添加中断请求的函数
  const handleCancelRequest = () => {
    console.log("用户取消请求?");
    if (abortControllerRef.current) {
      console.log("用户取消请求");
      abortControllerRef.current.abort();
    }
  };
  // ==================== Nodes ====================
  const placeholderNode = (
    <Space direction="vertical" size={16} style={{ paddingInline: "calc(calc(100% - 700px) /2)" }} className={styles.placeholder}>
      <Welcome
        variant="borderless"
        icon="https://kersen.tech/holyai.png"
        title="你好, 我是炣森科技HolyAI助手"
        description="专注服务政企数智化转型"
      />
      <Flex gap={16}>
        <Prompts
          items={[{
            key: '1',
            label: '关于炣森科技',
            children: [
              {
                key: '1-1',
                description: '公司介绍',
                icon: <span style={{ color: '#f93a4a', fontWeight: 700 }}>1</span>,
              },
              {
                key: '1-2',
                description: '公司创始人',
                icon: <span style={{ color: '#ff6565', fontWeight: 700 }}>2</span>,
              },
              {
                key: '1-3',
                description: '公司技术团队',
                icon: <span style={{ color: '#ff8f1f', fontWeight: 700 }}>3</span>,
              },
              {
                key: '1-4',
                description: '公司主要业务',
                icon: <span style={{ color: '#ff8f1f', fontWeight: 700 }}>4</span>,
              },
              {
                key: '1-5',
                description: '解决的痛点是什么',
                icon: <span style={{ color: '#ff8f1f', fontWeight: 700 }}>5</span>,
              },
              {
                key: '1-6',
                description: '解决方案有哪些',
                icon: <span style={{ color: '#ff8f1f', fontWeight: 700 }}>6</span>,
              },
              {
                key: '1-7',
                description: '行业案例分享',
                icon: <span style={{ color: '#ff8f1f', fontWeight: 700 }}>7</span>,
              }              
            ],
          }]}
          styles={{
            list: { height: '100%' },
            item: {
              flex: 1,
              backgroundImage: 'linear-gradient(123deg, #e5f4ff 0%, #efe7ff 100%)',
              borderRadius: 12,
              border: 'none',
            },
            subItem: { padding: 0, background: 'transparent' },
          }}
          onItemClick={(info) => {
            onSubmit(info.data.description as string);
          }}
          className={styles.chatPrompt}
        />

        <Prompts
          items={[{
            key: '2',
            label: '关于HolyAI',
            children: [
              {
                key: '2-1',
                icon: <FireOutlined style={{ color: '#FF4D4F' }} />,
                label: '常规问答',
                description: '你是谁',
              },
              {
                key: '2-2',
                icon: <ReadOutlined style={{ color: '#1890FF' }} />,
                label: '具备能力',
                description: 'HolyAI系统具备哪些能力',
              },
              {
                key: '2-3',
                icon: <SmileOutlined style={{ color: '#52C41A' }} />,
                label: '系统来源',
                description: '你来自哪里',
              },
            ],
          }]}
          styles={{
            item: {
              flex: 1,
              backgroundImage: 'linear-gradient(123deg, #e5f4ff 0%, #efe7ff 100%)',
              borderRadius: 12,
              border: 'none',
            },
            subItem: { background: '#ffffffa6' },
          }}
          onItemClick={(info) => {
            onSubmit(info.data.description as string);
          }}
          className={styles.chatPrompt}
        />
      </Flex>
    </Space>
  );

  const items: GetProp<typeof Bubble.List, "items"> = [...messages.map(
    ({ id, message, status }) => ({
      key: id,
      role: status === "local" ? "local" : "ai",
      content: message,
    })
  ),
  // 只有在agent请求中且尚未收到第一个数据块时才显示loading状态
  ...(agent.isRequesting() && !receivedFirstChunk ? [{
    key: 'loading-message',
    role: 'ai',
    content: '',
    loading: true,
  }] : [])
  ];
  
  // console.log("当前消息列表:", messages);
  // console.log("气泡项:", items);
  // console.log("是否收到第一个数据块:", receivedFirstChunk);

  // 在组件内部使用roles函数
  const currentRoles = roles(needtyping);

  const attachmentsNode = (
    <Badge dot={attachedFiles.length > 0 && !headerOpen}>
      <Button
        type="text"
        icon={<PaperClipOutlined />}
        onClick={() => setHeaderOpen(!headerOpen)}
      />
    </Badge>
  );

  const senderHeader = (
    <Sender.Header
      title="Attachments"
      open={headerOpen}
      onOpenChange={setHeaderOpen}
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <Attachments
        beforeUpload={() => false}
        items={attachedFiles}
        onChange={handleFileChange}
        placeholder={(type) =>
          type === "drop"
            ? { title: "Drop file here" }
            : {
                icon: <CloudUploadOutlined />,
                title: "上传文件",
                description: "点击或拖拽文件到此区域上传",
              }
        }
      />
    </Sender.Header>
  );

  const logoNode = (
    <div className={styles.logo}>
      <img
        src="./holyai.png"
        draggable={false}
        alt="logo"
      />
      <span>炣森科技HolyAI</span>
    </div>
  );

  const mobileLogoNode = (
    <div className={styles.mobileLogo}>
      <img
        src="./holyai.png"
        draggable={false}
        alt="logo"
      />
      <span>炣森科技HolyAI</span>
    </div>
  );

  const menuContent = (
    <>
      {logoNode}
      <Button
        onClick={onAddConversation}
        type="link"
        className={styles.addBtn}
        icon={<PlusOutlined />}
      >
        新建炣森AI对话
      </Button>
      <Conversations
        items={conversationsItems}
        className={styles.conversations}
        activeKey={activeKey}
        onActiveChange={onConversationClick}
      />
      <div className={styles.beianInfo}>
        <a href="https://beian.miit.gov.cn/#/Integrated/index" target="_blank" rel="noopener noreferrer">
          粤ICP备2025409489号
        </a>
      </div>
    </>
  );

  // ==================== Render =================
  return (
    <div className={styles.layout}>
      {/* 移动端顶部导航 */}
      <div className={styles.mobileHeader}>
        {mobileLogoNode}
        <Space>
          {/* <Button 
            type="text" 
            icon={<LoginOutlined />}
            onClick={handleLoginClick}
          /> */}
          <Button 
            type="text" 
            icon={drawerVisible ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={() => setDrawerVisible(!drawerVisible)}
          />
        </Space>
      </div>
      
      {/* 移动端抽屉菜单 */}
      <Drawer
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        placement="left"
        width={280}
        className={styles.drawerMenu}
        title="炣森科技HolyAI"
        footer={null}
      >
        {menuContent}
      </Drawer>
      
      {/* 桌面端左侧菜单 */}
      <div className={styles.menu}>
        {menuContent}
      </div>
      
      <div className={styles.chat}>
        {/* 🌟 消息列表 */}
        <Bubble.List
          items={[
            { content: placeholderNode, variant: "borderless" },
            ...items
          ]}
          roles={currentRoles}
          className={styles.messages}
        />
        {/* 🌟 提示词 */}
        <Prompts items={senderPromptsItems} onItemClick={onPromptsItemClick} />
        {/* 🌟 输入框 */}
        <Sender
          value={content}
          header={senderHeader}
          onSubmit={onSubmit}
          onChange={setContent}
          loading={agent.isRequesting()}
          className={styles.sender}
          onCancel={handleCancelRequest} // 添加取消回调
        />
      </div>
    </div>
  );
};

// 主应用组件，包含路由
const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* <Route path="/login" element={<Login onLoginSuccess={() => window.location.href = '/'} />} /> */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/" element={<AppContent />} />
      </Routes>
    </Router>
  );
};

export default App;
